import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import client, {
  API, formatApiError, getErrorOffender, fmtTime, fmtDay, dayKey, TYPE_LABEL, fmtHours,
  workedMinutes, hasUnclosedBreak, isoWeekStart, isoWeekId,
} from "../../lib/api";
import { ManualPunchModal, EditEventModal, BulkPunchModal } from "./EventModals";

// Replay the state machine over a set of events (sorted by ts) starting from `out`.
// Returns true if the sequence follows in → break_start → break_end → out cleanly.
function replayDayValid(events) {
  const sorted = [...events].sort((a, b) => new Date(a.ts) - new Date(b.ts));
  let state = "out";
  for (const e of sorted) {
    if (e.type === "in") {
      if (state !== "out") return false;
      state = "in";
    } else if (e.type === "out") {
      if (state !== "in" && state !== "break") return false;
      state = "out";
    } else if (e.type === "break_start") {
      if (state !== "in") return false;
      state = "break";
    } else if (e.type === "break_end") {
      if (state !== "break") return false;
      state = "in";
    }
  }
  return true;
}

function RecordsLog({ employees, onRefresh, pendingOffender, onOffenderConsumed }) {
  const [events, setEvents] = useState([]);
  const [payMarks, setPayMarks] = useState({}); // { empId: { weekId: {paid, note} } }
  const [openNote, setOpenNote] = useState(null); // "empId|weekId" combined key
  const [expandedEmp, setExpandedEmp] = useState({}); // { empId: true } — multi-week expansion
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [manualFor, setManualFor] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [cascadeAsk, setCascadeAsk] = useState(null); // { eventId, dayKey, employeeId, dayEvents }

  const reviewPunchRequest = async (evt, action) => {
    // Look up the punch_request that owns this provisional out
    try {
      const { data: list } = await client.get("/punch-requests", { params: { status: "pending" } });
      const req = list.find((r) => r.closing_out_event_id === evt.id);
      if (!req) {
        toast.error("Couldn't find the associated approval request");
        return;
      }
      await client.post(`/punch-requests/${req.id}/${action}`, { note: "" });
      toast.success(action === "approve" ? "Approved — punch-out is now permanent" : "Rejected — provisional punch-out removed");
      await load();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // When the owner confirms deletion, first predict whether the remaining events
  // for the same employee-on-that-day would still form a valid sequence. If not,
  // offer a cascade "Delete the whole day" option.
  const askDeleteEvent = (evt) => {
    const dk = dayKey(evt.ts);
    const dayRec = grouped[dk]?.byEmp?.[evt.employee_id];
    const remaining = (dayRec?.list || []).filter((x) => x.id !== evt.id);
    if (remaining.length && !replayDayValid(remaining)) {
      setCascadeAsk({
        eventId: evt.id,
        dayKey: dk,
        dayLabel: dayRec ? fmtDay(evt.ts) : dk,
        employeeId: evt.employee_id,
        employeeName: evt.employee_name || dayRec?.name || "this employee",
        remainingCount: remaining.length + 1, // includes the one being deleted
      });
      setDeletingEventId(null);
      return;
    }
    deleteEvent(evt.id);
  };

  const deleteEvent = async (id) => {
    try {
      await client.delete(`/events/${id}`);
      toast.success("Deleted");
      setDeletingEventId(null);
      setCascadeAsk(null);
      await load();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteWholeDay = async () => {
    if (!cascadeAsk) return;
    try {
      const { data } = await client.delete("/events/day", {
        params: {
          employee_id: cascadeAsk.employeeId,
          day: cascadeAsk.dayKey,
          tz_offset_min: -new Date().getTimezoneOffset(),
        },
      });
      toast.success(`Deleted ${data.deleted || 0} entries`);
      setCascadeAsk(null);
      setDeletingEventId(null);
      await load();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Default range: full current ISO week (Mon → Sun) so the header reads "This week"
  const defaultFrom = useMemo(() => {
    const d = isoWeekStart();
    return d.toISOString().slice(0, 10);
  }, []);
  const defaultTo = useMemo(() => {
    const d = isoWeekStart();
    d.setUTCDate(d.getUTCDate() + 6);
    return d.toISOString().slice(0, 10);
  }, []);

  const [filters, setFilters] = useState({
    from: defaultFrom,
    to: defaultTo,
    employeeId: "all",
  });

  const fromIso = useMemo(
    () => (filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : ""),
    [filters.from]
  );
  const toIso = useMemo(
    () => (filters.to ? new Date(`${filters.to}T23:59:59.999`).toISOString() : ""),
    [filters.to]
  );

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (fromIso) params.date_from = fromIso;
      if (toIso) params.date_to = toIso;
      if (filters.employeeId && filters.employeeId !== "all")
        params.employee_id = filters.employeeId;
      const { data } = await client.get("/events", { params });
      setEvents(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [fromIso, toIso, filters.employeeId]);

  useEffect(() => { load(); }, [load]);

  // When we're navigated in from the Now tab with a "fix this blocker" offender,
  // widen the date range so the row is guaranteed visible, then wait for the
  // fresh /events response to include it, then scroll + arm delete.
  useEffect(() => {
    if (!pendingOffender) return;
    const offTs = pendingOffender.ts || "";
    if (!offTs) { onOffenderConsumed && onOffenderConsumed(); return; }
    // Widen filters to the offender's LOCAL day (not UTC — matches how day cards render)
    const off = new Date(offTs);
    const offDay = `${off.getFullYear()}-${String(off.getMonth() + 1).padStart(2, "0")}-${String(off.getDate()).padStart(2, "0")}`;
    setFilters((f) => {
      const from = f.from && f.from <= offDay ? f.from : offDay;
      const to = f.to && f.to >= offDay ? f.to : offDay;
      return { ...f, from, to, employeeId: "all" };
    });
  }, [pendingOffender]); // eslint-disable-line react-hooks/exhaustive-deps

  // Second effect: fires whenever `events` changes. Once the offender row is in
  // the DOM, scroll + arm its delete-confirm. Also handles the case where the
  // offender no longer exists (e.g. someone else deleted it in the meantime).
  useEffect(() => {
    if (!pendingOffender) return;
    const present = events.some((e) => e.id === pendingOffender.id);
    if (!present) {
      // Wait one more render tick in case /events is still in-flight
      if (!busy) {
        toast.error("That record no longer exists — it may have been deleted already. Try the punch again.");
        onOffenderConsumed && onOffenderConsumed();
      }
      return;
    }
    // It's in the list. Wait one paint so the row is in the DOM, then scroll.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="event-row-${pendingOffender.id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setDeletingEventId(pendingOffender.id);
        toast("Offender highlighted — tap Confirm to delete", { duration: 6000 });
      }
      onOffenderConsumed && onOffenderConsumed();
    }, 120);
    return () => clearTimeout(t);
  }, [events, pendingOffender, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilters =
    (filters.employeeId !== "all" ? 1 : 0) +
    (filters.from !== defaultFrom ? 1 : 0) +
    (filters.to !== defaultTo ? 1 : 0);

  const resetFilters = () =>
    setFilters({ from: defaultFrom, to: defaultTo, employeeId: "all" });

  // Week navigation helpers
  // Local YYYY-MM-DD anchor — using UTC's toISOString().slice(0,10) here bites
  // east-of-UTC users at ISO week boundaries (e.g. Mon 9am AEST is Sun UTC).
  const localTodayIso = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
  };
  const weekBoundsFromDate = (dateStr) => {
    const anchor = new Date(dateStr + "T00:00:00Z");
    const mon = new Date(anchor);
    const dayNum = mon.getUTCDay() || 7;
    mon.setUTCDate(mon.getUTCDate() - dayNum + 1);
    const sun = new Date(mon);
    sun.setUTCDate(sun.getUTCDate() + 6);
    return {
      from: mon.toISOString().slice(0, 10),
      to: sun.toISOString().slice(0, 10),
    };
  };

  const isWholeWeek = useMemo(() => {
    if (!filters.from || !filters.to) return false;
    const { from, to } = weekBoundsFromDate(filters.from);
    return from === filters.from && to === filters.to;
  }, [filters.from, filters.to]);

  const shiftWeek = (delta) => {
    const anchor = new Date((filters.from || defaultFrom) + "T00:00:00Z");
    anchor.setUTCDate(anchor.getUTCDate() + delta * 7);
    const { from, to } = weekBoundsFromDate(anchor.toISOString().slice(0, 10));
    setFilters((f) => ({ ...f, from, to }));
  };

  const jumpToCurrentWeek = () => {
    const { from, to } = weekBoundsFromDate(localTodayIso());
    setFilters((f) => ({ ...f, from, to }));
  };

  const jumpToLast4Weeks = () => {
    const { from: thisFrom, to: thisTo } = weekBoundsFromDate(localTodayIso());
    // 3 weeks back from thisFrom
    const start = new Date(thisFrom + "T00:00:00Z");
    start.setUTCDate(start.getUTCDate() - 21);
    setFilters((f) => ({ ...f, from: start.toISOString().slice(0, 10), to: thisTo }));
  };

  const weekLabel = useMemo(() => {
    if (!filters.from || !filters.to) return "";
    const { from, to } = weekBoundsFromDate(filters.from);
    const f = new Date(from + "T00:00:00");
    const t = new Date(to + "T00:00:00");
    const sameMonth = f.getMonth() === t.getMonth();
    const opts = { day: "numeric", month: "short" };
    const fs = f.toLocaleDateString("en-AU", opts);
    const ts = sameMonth
      ? t.toLocaleDateString("en-AU", { day: "numeric" })
      : t.toLocaleDateString("en-AU", opts);
    return `${fs}–${ts} ${t.getFullYear()}`;
  }, [filters.from]);

  const currentWeekBounds = useMemo(
    () => weekBoundsFromDate(localTodayIso()),
    []
  );
  const isCurrentWeek = isWholeWeek && filters.from === currentWeekBounds.from;

  // ISO weeks fully covered by the current range (Mon-Sun inside from/to).
  // 1 week → single-row Mark paid / Note; 2+ weeks → expandable per-week panel.
  const visibleWeeks = useMemo(() => {
    if (!filters.from || !filters.to) return [];
    const out = [];
    const fromD = new Date(filters.from + "T00:00:00Z");
    const toD = new Date(filters.to + "T00:00:00Z");
    const d = new Date(fromD);
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() - dayNum + 1);
    while (true) {
      const mon = new Date(d);
      const sun = new Date(d);
      sun.setUTCDate(sun.getUTCDate() + 6);
      if (sun > toD) break;
      if (mon >= fromD) {
        const monIso = mon.toISOString().slice(0, 10);
        const sunIso = sun.toISOString().slice(0, 10);
        const f = new Date(monIso + "T00:00:00");
        const t = new Date(sunIso + "T00:00:00");
        const opts = { day: "numeric", month: "short" };
        const label =
          f.getMonth() === t.getMonth()
            ? `${f.toLocaleDateString("en-AU", { day: "numeric" })}–${t.toLocaleDateString("en-AU", opts)}`
            : `${f.toLocaleDateString("en-AU", opts)}–${t.toLocaleDateString("en-AU", opts)}`;
        out.push({ weekId: isoWeekId(mon), monday: monIso, sunday: sunIso, label });
      }
      d.setUTCDate(d.getUTCDate() + 7);
    }
    return out;
  }, [filters.from, filters.to]);
  const visibleWeekIds = useMemo(() => visibleWeeks.map((w) => w.weekId), [visibleWeeks]);
  const visibleWeekIdsKey = visibleWeekIds.join(",");
  const isMultiWeek = visibleWeeks.length > 1;
  const singleWeekId = visibleWeeks.length === 1 ? visibleWeeks[0].weekId : "";

  // Fetch pay marks for every visible week — shape: { empId: { weekId: {paid, note} } }
  useEffect(() => {
    if (!visibleWeekIds.length) { setPayMarks({}); return; }
    let cancelled = false;
    Promise.all(
      visibleWeekIds.map((wid) =>
        client
          .get("/pay-marks", { params: { week_id: wid } })
          .then(({ data }) => [wid, data || []])
          .catch(() => [wid, []])
      )
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      for (const [wid, list] of results) {
        for (const m of list) {
          if (!map[m.employee_id]) map[m.employee_id] = {};
          map[m.employee_id][wid] = { paid: !!m.paid, note: m.note || "" };
        }
      }
      setPayMarks(map);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleWeekIdsKey]);

  const savePayMark = async (employeeId, weekId, patch) => {
    if (!weekId) return;
    const before = payMarks[employeeId]?.[weekId] || { paid: false, note: "" };
    const next = { ...before, ...patch };
    setPayMarks((prev) => ({
      ...prev,
      [employeeId]: { ...(prev[employeeId] || {}), [weekId]: next },
    }));
    try {
      await client.post("/pay-marks", {
        employee_id: employeeId,
        week_id: weekId,
        paid: next.paid,
        note: next.note,
      });
    } catch (e) {
      setPayMarks((prev) => ({
        ...prev,
        [employeeId]: { ...(prev[employeeId] || {}), [weekId]: before },
      }));
      toast.error(formatApiError(e));
    }
  };

  const togglePaid = (employeeId, weekId) => {
    const current = payMarks[employeeId]?.[weekId]?.paid || false;
    savePayMark(employeeId, weekId, { paid: !current });
    toast.success(!current ? "Marked paid" : "Unmarked");
  };

  const rangeLabel = useMemo(() => {
    if (!filters.from && !filters.to) return "All time";
    if (filters.from === defaultFrom && filters.to === defaultTo) return "This week";
    const fmt = (s) =>
      s
        ? new Date(s + "T00:00:00").toLocaleDateString("en-AU", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "…";
    return `${fmt(filters.from)} → ${fmt(filters.to)}`;
  }, [filters, defaultFrom, defaultTo]);

  const exportCsv = () => {
    const token = localStorage.getItem("owner_token");
    const parts = [`token=${token}`];
    if (fromIso) parts.push(`date_from=${encodeURIComponent(fromIso)}`);
    if (toIso) parts.push(`date_to=${encodeURIComponent(toIso)}`);
    window.open(`${API}/events/export?${parts.join("&")}`, "_blank");
  };

  const deleteFiltered = async () => {
    setBusy(true);
    try {
      const params = {};
      if (fromIso) params.date_from = fromIso;
      if (toIso) params.date_to = toIso;
      const { data } = await client.delete("/events", { params });
      toast.success(`Deleted ${data.deleted} entries`);
      setConfirming(false);
      await load();
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Group: day → employee → events (sorted asc for worked calc)
  const grouped = useMemo(() => {
    const days = {};
    const sorted = [...events].sort((a, b) => new Date(a.ts) - new Date(b.ts));
    for (const e of sorted) {
      const dk = dayKey(e.ts);
      days[dk] = days[dk] || { label: fmtDay(e.ts), ts: e.ts, byEmp: {} };
      const key = e.employee_id;
      days[dk].byEmp[key] = days[dk].byEmp[key] || { name: e.employee_name, branch: e.branch, list: [] };
      days[dk].byEmp[key].list.push(e);
    }
    return days;
  }, [events]);

  // Per-employee totals across the whole filtered range (for payroll) + per-week breakdown
  const payroll = useMemo(() => {
    const totals = {};
    Object.values(grouped).forEach((day) => {
      Object.entries(day.byEmp).forEach(([id, rec]) => {
        const mins = workedMinutes(rec.list);
        // Bucket by LOCAL date to match how day cards are grouped via dayKey().
        // Using UTC (`.toISOString().slice(0,10)`) would put an early-morning
        // Mon-10-Aug punch (which is late-Sun-9-Aug in UTC for AEST/+10 users)
        // into the previous week and silently drop it from the payroll total.
        const firstD = new Date(rec.list[0].ts);
        const dayIso = `${firstD.getFullYear()}-${String(firstD.getMonth() + 1).padStart(2, "0")}-${String(firstD.getDate()).padStart(2, "0")}`;
        const wk = visibleWeeks.find((w) => w.monday <= dayIso && dayIso <= w.sunday);
        if (!totals[id]) totals[id] = { id, name: rec.name, branch: rec.branch, minutes: 0, byWeek: {} };
        totals[id].minutes += mins;
        if (wk) totals[id].byWeek[wk.weekId] = (totals[id].byWeek[wk.weekId] || 0) + mins;
      });
    });
    return Object.values(totals).sort((a, b) => b.minutes - a.minutes);
  }, [grouped, visibleWeeks]);

  const copyPayroll = () => {
    if (payroll.length === 0) return;
    const lines = payroll.map((p) => `${p.name} (${p.branch}): ${fmtHours(p.minutes)}`);
    lines.unshift(`Payroll · ${rangeLabel}`);
    const text = lines.join("\n");
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success("Copied — paste into email or a note"))
      .catch(() => toast.error("Couldn't copy"));
  };

  const dayKeys = useMemo(
    () => Object.keys(grouped).sort((a, b) => new Date(grouped[b].ts) - new Date(grouped[a].ts)),
    [grouped]
  );

  const inputCls =
    "w-full px-3 py-2 bg-panel-soft text-ivory border border-line rounded-lg text-[14px] focus:outline-none";

  return (
    <div>
      {/* Week navigator — primary way to browse & delete week-by-week */}
      <div className="bg-panel border border-gold-deep/40 rounded-xl p-3 mb-4" data-testid="week-nav">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => shiftWeek(-1)}
            data-testid="week-prev"
            className="rounded-full border border-line text-ivory px-3 py-2 text-sm hover:border-gold-deep transition-colors"
            aria-label="Previous week"
          >
            ◀
          </button>
          <div className="flex-1 text-center">
            <div className="font-serif text-lg text-ivory leading-tight" data-testid="week-label">
              {isWholeWeek ? weekLabel : "Custom range"}
            </div>
            <div className="caps text-muted-jewel mt-0.5">
              {isCurrentWeek ? "This week" : isWholeWeek ? "Week view" : "Filtered"}
            </div>
          </div>
          <button
            onClick={() => shiftWeek(1)}
            data-testid="week-next"
            className="rounded-full border border-line text-ivory px-3 py-2 text-sm hover:border-gold-deep transition-colors"
            aria-label="Next week"
          >
            ▶
          </button>
        </div>
        <div className="flex justify-end mb-3">
          <button
            onClick={() => setShowBulk(true)}
            data-testid="bulk-fill-open"
            className="rounded-full border border-gold-deep text-gold px-4 py-1.5 text-[13px] font-semibold hover:bg-gold-deep/10 transition-colors"
          >
            + Bulk fill day
          </button>
        </div>
        {!isCurrentWeek && (
          <button
            onClick={jumpToCurrentWeek}
            data-testid="week-jump-current"
            className="w-full mt-2 text-gold text-[13px] hover:opacity-80 transition-opacity"
          >
            Jump to this week
          </button>
        )}
        <button
          onClick={jumpToLast4Weeks}
          data-testid="jump-last-4-weeks"
          className="w-full mt-2 rounded-full border border-line text-gold text-[13px] py-1.5 hover:border-gold-deep transition-colors"
        >
          Show last 4 weeks · monthly payout
        </button>
      </div>

      {/* Focus banner when a single employee is selected — reassures the owner that other staff are hidden */}
      {filters.employeeId !== "all" && (
        <div
          data-testid="focus-banner"
          className="bg-panel-soft border border-gold-deep rounded-xl px-4 py-2.5 mb-3 flex items-center justify-between gap-3"
        >
          <div className="text-[13px] text-ivory">
            <span className="text-gold caps text-[11px] mr-2">Focus</span>
            Showing only <b>{employees.find((e) => e.id === filters.employeeId)?.name || "one employee"}</b> — other staff are hidden from this view.
          </div>
          <button
            onClick={() => setFilters((f) => ({ ...f, employeeId: "all" }))}
            data-testid="focus-clear"
            className="text-muted-jewel text-[12px] hover:text-ivory transition-colors shrink-0"
          >
            Clear
          </button>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-panel border border-line rounded-xl p-4 mb-5" data-testid="records-filters">
        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="caps text-muted-jewel block mb-1.5">From</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              data-testid="records-from"
              className={inputCls}
            />
          </div>
          <div>
            <label className="caps text-muted-jewel block mb-1.5">To</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              data-testid="records-to"
              className={inputCls}
            />
          </div>
        </div>
        <div className="mt-2.5">
          <label className="caps text-muted-jewel block mb-1.5">Employee</label>
          <select
            value={filters.employeeId}
            onChange={(e) => setFilters((f) => ({ ...f, employeeId: e.target.value }))}
            data-testid="records-employee"
            className={inputCls}
          >
            <option value="all">All employees</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name} · {emp.branch}
              </option>
            ))}
          </select>
        </div>
        {activeFilters > 0 && (
          <button
            onClick={resetFilters}
            data-testid="records-reset"
            className="mt-3 text-gold text-[13px] hover:opacity-80 transition-opacity"
          >
            ← Reset to this week
          </button>
        )}
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="text-muted-jewel text-[13px]" data-testid="records-header">
          {rangeLabel} · {events.length} entries
        </div>
        <button
          onClick={exportCsv}
          data-testid="records-export"
          className="rounded-full border border-line text-gold px-4 py-1.5 text-[13px] hover:border-gold-deep transition-colors"
        >
          Download CSV
        </button>
      </div>

      {dayKeys.length === 0 && (
        <div className="text-muted-jewel text-sm py-5" data-testid="records-empty">
          {busy ? "Loading…" : "No punches recorded in this range."}
        </div>
      )}

      {payroll.length > 0 && (
        <div className="bg-panel border border-gold-deep/40 rounded-xl p-4 mb-6" data-testid="payroll-summary">
          <div className="flex items-center justify-between mb-3">
            <div className="caps text-gold">
              Payroll summary{isWholeWeek ? ` · ${weekLabel}` : isMultiWeek ? ` · ${visibleWeeks.length} weeks` : ""}
            </div>
            <button
              onClick={copyPayroll}
              data-testid="payroll-copy"
              className="text-gold text-[13px] hover:opacity-80 transition-opacity"
            >
              Copy
            </button>
          </div>
          {payroll.map((p, i) => {
            const singleMark = singleWeekId
              ? (payMarks[p.id]?.[singleWeekId] || { paid: false, note: "" })
              : { paid: false, note: "" };
            const singleKey = singleWeekId ? `${p.id}|${singleWeekId}` : "";
            const isPaid = singleMark.paid;
            const noteOpen = openNote === singleKey;
            const paidCount = visibleWeeks.filter((w) => payMarks[p.id]?.[w.weekId]?.paid).length;
            const allPaid = isMultiWeek && paidCount === visibleWeeks.length;
            const isExpanded = !!expandedEmp[p.id];
            return (
              <div key={i} className="py-1.5 border-b border-line last:border-0" data-testid={`payroll-row-${i}`}>
                <div className="flex justify-between items-center text-sm">
                  <span className="min-w-0 flex-1">
                    {p.name}
                    <span className="text-muted-jewel text-[13px] ml-2">· {p.branch}</span>
                  </span>
                  <span className="text-gold font-mono mr-2">{fmtHours(p.minutes)}</span>
                  {isWholeWeek && !isMultiWeek && (
                    <>
                      <button
                        onClick={() => togglePaid(p.id, singleWeekId)}
                        data-testid={`payroll-paid-${p.id}`}
                        className={`text-[12px] rounded-full px-3 py-1 border transition-colors mr-1.5 ${
                          isPaid
                            ? "border-jewel-green text-jewel-green"
                            : "border-line text-muted-jewel hover:text-ivory hover:border-gold-deep"
                        }`}
                      >
                        {isPaid ? "✓ Paid" : "Mark paid"}
                      </button>
                      <button
                        onClick={() => setOpenNote(noteOpen ? null : singleKey)}
                        data-testid={`payroll-note-toggle-${p.id}`}
                        className={`text-[12px] rounded-full px-3 py-1 border transition-colors ${
                          singleMark.note
                            ? "border-gold text-gold"
                            : "border-line text-muted-jewel hover:text-ivory hover:border-gold-deep"
                        }`}
                      >
                        {singleMark.note ? "✎ Note" : "+ Note"}
                      </button>
                    </>
                  )}
                  {isMultiWeek && (
                    <>
                      <span
                        data-testid={`payroll-paid-count-${p.id}`}
                        className={`text-[12px] rounded-full px-3 py-1 border mr-1.5 ${
                          allPaid
                            ? "border-jewel-green text-jewel-green"
                            : paidCount > 0
                              ? "border-gold text-gold"
                              : "border-line text-muted-jewel"
                        }`}
                      >
                        {paidCount}/{visibleWeeks.length} paid
                      </span>
                      <button
                        onClick={() => setExpandedEmp((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
                        data-testid={`payroll-expand-${p.id}`}
                        aria-expanded={isExpanded}
                        className="text-[12px] rounded-full px-3 py-1 border border-line text-muted-jewel hover:text-ivory hover:border-gold-deep transition-colors"
                      >
                        {isExpanded ? "Hide weeks" : "Weeks"}
                      </button>
                    </>
                  )}
                </div>
                {isWholeWeek && !isMultiWeek && (noteOpen || singleMark.note) && (
                  <div className="mt-2 pl-1" data-testid={`payroll-note-panel-${p.id}`}>
                    <textarea
                      value={singleMark.note}
                      onChange={(e) =>
                        setPayMarks((prev) => ({
                          ...prev,
                          [p.id]: {
                            ...(prev[p.id] || {}),
                            [singleWeekId]: { ...(prev[p.id]?.[singleWeekId] || { paid: false }), note: e.target.value.slice(0, 500) },
                          },
                        }))
                      }
                      onBlur={() => savePayMark(p.id, singleWeekId, { note: (payMarks[p.id]?.[singleWeekId]?.note || "").trim() })}
                      placeholder="Amendment, bonus, deduction, note to accountant…"
                      data-testid={`payroll-note-input-${p.id}`}
                      rows={2}
                      maxLength={500}
                      className="w-full px-3 py-2 bg-panel-soft text-ivory border border-line rounded-lg text-[13px] focus:outline-none resize-y"
                    />
                    <div className="flex justify-between text-[11px] text-muted-jewel mt-1">
                      <span>Saved on blur · {(singleMark.note || "").length}/500</span>
                      {singleMark.note && (
                        <button
                          onClick={() => {
                            setPayMarks((prev) => ({
                              ...prev,
                              [p.id]: { ...(prev[p.id] || {}), [singleWeekId]: { ...(prev[p.id]?.[singleWeekId] || { paid: false }), note: "" } },
                            }));
                            savePayMark(p.id, singleWeekId, { note: "" });
                          }}
                          data-testid={`payroll-note-clear-${p.id}`}
                          className="text-jewel-red hover:opacity-80 transition-opacity"
                        >
                          Clear note
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {isMultiWeek && isExpanded && (
                  <div className="mt-2 space-y-1.5" data-testid={`payroll-weeks-${p.id}`}>
                    {visibleWeeks.map((w) => {
                      const wMark = payMarks[p.id]?.[w.weekId] || { paid: false, note: "" };
                      const wMins = p.byWeek?.[w.weekId] || 0;
                      const wPaid = wMark.paid;
                      const wKey = `${p.id}|${w.weekId}`;
                      const wNoteOpen = openNote === wKey;
                      return (
                        <div
                          key={w.weekId}
                          className="pl-3 border-l-2 border-line"
                          data-testid={`payroll-week-${p.id}-${w.weekId}`}
                        >
                          <div className="flex justify-between items-center text-[13px] gap-2">
                            <span className="text-muted-jewel flex-1 min-w-0 truncate">{w.label}</span>
                            <span className="text-gold font-mono">{fmtHours(wMins)}</span>
                            <button
                              onClick={() => togglePaid(p.id, w.weekId)}
                              data-testid={`payroll-paid-${p.id}-${w.weekId}`}
                              className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                                wPaid
                                  ? "border-jewel-green text-jewel-green"
                                  : "border-line text-muted-jewel hover:text-ivory hover:border-gold-deep"
                              }`}
                            >
                              {wPaid ? "✓ Paid" : "Mark paid"}
                            </button>
                            <button
                              onClick={() => setOpenNote(wNoteOpen ? null : wKey)}
                              data-testid={`payroll-note-toggle-${p.id}-${w.weekId}`}
                              className={`text-[11px] rounded-full px-2.5 py-1 border transition-colors ${
                                wMark.note
                                  ? "border-gold text-gold"
                                  : "border-line text-muted-jewel hover:text-ivory hover:border-gold-deep"
                              }`}
                            >
                              {wMark.note ? "✎ Note" : "+ Note"}
                            </button>
                          </div>
                          {(wNoteOpen || wMark.note) && (
                            <div className="mt-1.5" data-testid={`payroll-note-panel-${p.id}-${w.weekId}`}>
                              <textarea
                                value={wMark.note}
                                onChange={(e) =>
                                  setPayMarks((prev) => ({
                                    ...prev,
                                    [p.id]: {
                                      ...(prev[p.id] || {}),
                                      [w.weekId]: { ...(prev[p.id]?.[w.weekId] || { paid: false }), note: e.target.value.slice(0, 500) },
                                    },
                                  }))
                                }
                                onBlur={() => savePayMark(p.id, w.weekId, { note: (payMarks[p.id]?.[w.weekId]?.note || "").trim() })}
                                placeholder="Amendment, bonus, deduction, note to accountant…"
                                data-testid={`payroll-note-input-${p.id}-${w.weekId}`}
                                rows={2}
                                maxLength={500}
                                className="w-full px-3 py-2 bg-panel-soft text-ivory border border-line rounded-lg text-[12px] focus:outline-none resize-y"
                              />
                              <div className="flex justify-between text-[10px] text-muted-jewel mt-1">
                                <span>Saved on blur · {(wMark.note || "").length}/500</span>
                                {wMark.note && (
                                  <button
                                    onClick={() => {
                                      setPayMarks((prev) => ({
                                        ...prev,
                                        [p.id]: { ...(prev[p.id] || {}), [w.weekId]: { ...(prev[p.id]?.[w.weekId] || { paid: false }), note: "" } },
                                      }));
                                      savePayMark(p.id, w.weekId, { note: "" });
                                    }}
                                    data-testid={`payroll-note-clear-${p.id}-${w.weekId}`}
                                    className="text-jewel-red hover:opacity-80 transition-opacity"
                                  >
                                    Clear note
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {(isWholeWeek || isMultiWeek) && (
            <div className="text-[12px] text-muted-jewel mt-3">
              Payments are tracked per week and per employee. Marks are private to the owner.
            </div>
          )}
        </div>
      )}

      {dayKeys.map((dk) => (
        <div key={dk} className="mb-6" data-testid={`records-day-${dk}`}>
          <div className="caps text-gold mb-2">{grouped[dk].label}</div>
          {Object.entries(grouped[dk].byEmp).map(([id, rec]) => {
            const mins = workedMinutes(rec.list);
            return (
              <div key={id} className="bg-panel border border-line rounded-xl px-4 py-3.5 mb-2.5">
                <div className="flex justify-between mb-2">
                  <span className="font-semibold">
                    {rec.name}{" "}
                    <span className="text-muted-jewel font-normal text-[13px]">· {rec.branch}</span>
                  </span>
                  <span className="inline-flex items-center gap-2 flex-wrap justify-end">
                    {mins > 14 * 60 && (
                      <span
                        data-testid={`long-shift-${id}-${dk}`}
                        title="This day's recorded hours exceed 14h — likely a forgotten punch-out. Edit or delete the offending punch before marking paid."
                        className="text-[10px] uppercase tracking-widest text-jewel-red border border-jewel-red/60 rounded-full px-2 py-0.5"
                      >
                        ⚠ {Math.round(mins / 60)}h — check
                      </span>
                    )}
                    {hasUnclosedBreak(rec.list) && (
                      <span
                        data-testid={`unclosed-break-${id}-${dk}`}
                        title="A break was never ended. The app auto-closed it at the punch-out."
                        className="text-[10px] uppercase tracking-widest text-jewel-amber border border-jewel-amber/50 rounded-full px-2 py-0.5"
                      >
                        Break not closed
                      </span>
                    )}
                    <span className="text-gold text-sm" data-testid={`worked-${id}-${dk}`}>{fmtHours(mins)}</span>
                  </span>
                </div>
                {rec.list.map((e) => {
                  const confirmingDelete = deletingEventId === e.id;
                  return (
                    <div
                      key={e.id}
                      className="w-full flex justify-between items-center text-[13px] text-muted-jewel py-1"
                      data-testid={`event-row-${e.id}`}
                    >
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        {TYPE_LABEL[e.type]}
                        {e.manual && <span className="text-[10px] uppercase tracking-widest text-gold">Manual</span>}
                        {e.edited && <span className="text-[10px] uppercase tracking-widest text-muted-jewel">Edited</span>}
                        {e.pending_approval && (
                          <span
                            data-testid={`pending-chip-${e.id}`}
                            className="text-[10px] uppercase tracking-widest text-jewel-amber border border-jewel-amber rounded-full px-1.5 py-[1px]"
                            title="Employee requested this via kiosk — awaiting your approval"
                          >
                            ⏳ Pending
                          </span>
                        )}
                        {Array.isArray(e.previous_names) && e.previous_names.length > 0 && (
                          <span
                            className="text-[10px] uppercase tracking-widest text-muted-jewel italic normal-case"
                            title={`Previously named: ${e.previous_names.join(" → ")}`}
                            data-testid={`prev-names-${e.id}`}
                          >
                            was {e.previous_names[e.previous_names.length - 1]}
                          </span>
                        )}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <span>{fmtTime(e.ts)}</span>
                        {e.pending_approval && (
                          <>
                            <button
                              onClick={() => reviewPunchRequest(e, "approve")}
                              data-testid={`pending-approve-${e.id}`}
                              className="text-jewel-green text-[12px] hover:opacity-80 transition-opacity"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => reviewPunchRequest(e, "reject")}
                              data-testid={`pending-reject-${e.id}`}
                              className="text-jewel-amber text-[12px] hover:opacity-80 transition-opacity"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setEditingEvent(e)}
                          data-testid={`event-edit-${e.id}`}
                          className="text-gold text-[12px] hover:opacity-80 transition-opacity"
                        >
                          Edit
                        </button>
                        {confirmingDelete ? (
                          <>
                            <button
                              onClick={() => askDeleteEvent(e)}
                              data-testid={`event-delete-yes-${e.id}`}
                              className="text-jewel-red text-[12px] font-semibold hover:opacity-80 transition-opacity"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setDeletingEventId(null)}
                              data-testid={`event-delete-cancel-${e.id}`}
                              className="text-muted-jewel text-[12px] hover:text-ivory transition-colors"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setDeletingEventId(e.id)}
                            data-testid={`event-delete-${e.id}`}
                            className="text-jewel-red text-[12px] hover:opacity-80 transition-opacity"
                          >
                            Delete
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
                <button
                  onClick={() => setManualFor({ id, name: rec.name, branch: rec.branch })}
                  data-testid={`add-missing-${id}-${dk}`}
                  className="mt-2 text-gold text-[12px] hover:opacity-80 transition-opacity"
                >
                  + Add missing punch
                </button>
              </div>
            );
          })}
        </div>
      ))}

      <div className="mt-7 border-t border-line pt-5">
        {!confirming ? (
          <button
            onClick={() => setConfirming(true)}
            data-testid="delete-records-btn"
            disabled={events.length === 0}
            className="seal-danger w-full rounded-xl py-3 text-sm disabled:opacity-40"
          >
            {isWholeWeek ? `Delete week (${weekLabel})` : "Delete records in this range"}
          </button>
        ) : (
          <div className="bg-panel border border-jewel-red rounded-xl p-4.5" data-testid="delete-records-confirm">
            <div className="text-sm mb-1.5">
              Delete all {events.length} entries for {rangeLabel.toLowerCase()}?
            </div>
            <div className="text-[13px] text-muted-jewel mb-3.5">
              This can't be undone. Download the CSV first if you need a copy for payroll.
              {filters.employeeId !== "all" && (
                <> Note: employee filter is ignored — this deletes every entry in the date range.</>
              )}
            </div>
            <div className="flex gap-2.5">
              <button
                onClick={deleteFiltered}
                disabled={busy}
                data-testid="delete-records-yes"
                className="bg-jewel-red text-white rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-40"
              >
                Yes, delete
              </button>
              <button
                onClick={() => setConfirming(false)}
                data-testid="delete-records-cancel"
                className="rounded-full border border-line text-muted-jewel px-5 py-2.5 text-sm hover:text-ivory transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {editingEvent && (
        <EditEventModal
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={() => { setEditingEvent(null); load(); onRefresh && onRefresh(); }}
          onOffenderJump={(offender) => {
            setEditingEvent(null);
            setDeletingEventId(offender.id);
            // Scroll the offender row into view after a paint
            setTimeout(() => {
              const el = document.querySelector(`[data-testid="event-row-${offender.id}"]`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 60);
          }}
        />
      )}
      {manualFor && (
        <ManualPunchModal
          employee={manualFor}
          onClose={() => setManualFor(null)}
          onSaved={() => { setManualFor(null); load(); onRefresh && onRefresh(); }}
          onFillDay={(p) => { setManualFor(null); setShowBulk(p); }}
          onOffenderJump={(offender) => {
            setManualFor(null);
            setDeletingEventId(offender.id);
            setTimeout(() => {
              const el = document.querySelector(`[data-testid="event-row-${offender.id}"]`);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 60);
          }}
        />
      )}
      {showBulk && (
        <BulkPunchModal
          employees={employees}
          prefill={showBulk === true ? null : showBulk}
          onClose={() => setShowBulk(false)}
          onSaved={() => { setShowBulk(false); load(); onRefresh && onRefresh(); }}
        />
      )}

      {cascadeAsk && (
        <div
          role="dialog"
          data-testid="cascade-delete-modal"
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setCascadeAsk(null)}
        >
          <div
            onClick={(ev) => ev.stopPropagation()}
            className="bg-panel border border-jewel-red rounded-2xl p-5 max-w-[420px] w-full shadow-xl"
          >
            <div className="caps text-jewel-red mb-2">Sequence would break</div>
            <div className="text-sm text-ivory mb-2">
              Deleting this punch leaves <span className="font-semibold">{cascadeAsk.employeeName}</span>'s
              day of <span className="font-semibold">{cascadeAsk.dayLabel}</span> in an invalid state
              (an <em>out</em> without an <em>in</em>, or a hanging break).
            </div>
            <div className="text-[13px] text-muted-jewel mb-4">
              You can delete just this row and fix the rest manually, or wipe the whole day
              ({cascadeAsk.remainingCount} entries) and re-enter it cleanly.
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={deleteWholeDay}
                data-testid="cascade-delete-day"
                className="bg-jewel-red text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Delete whole day ({cascadeAsk.remainingCount} entries)
              </button>
              <button
                onClick={() => deleteEvent(cascadeAsk.eventId)}
                data-testid="cascade-delete-one"
                className="seal-danger rounded-full px-4 py-2.5 text-sm"
              >
                Delete only this row
              </button>
              <button
                onClick={() => setCascadeAsk(null)}
                data-testid="cascade-cancel"
                className="rounded-full border border-line text-muted-jewel px-4 py-2.5 text-sm hover:text-ivory transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default RecordsLog;
